import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import {
  patchFromProjectRead,
  stagesFromSets,
  toActivityEntry,
  toStage,
  toStageSet,
} from '@/shared/api/adapt'
import { api } from '@/shared/api/client'
import type { AuditEntryRead, ProjectRead, StageSetRead } from '@/shared/api/contracts'
import { qk } from '@/shared/api/query-keys'
import { errorMessage } from '@/shared/components/data'
import { useUserIndex } from '@/shared/hooks/use-users'
import { useT } from '@/shared/i18n'
import type { ActivityEntry, ProjectDetail, Stage } from '@/shared/types/domain'

import { useCompany, useCompanyContacts, useCompanyProjects } from '@/features/companies/api'

/**
 * Las fases de un tipo.
 *
 * `GET /stages?kind=` **no existe**. Lo que existe es `GET /stage-sets?kind=`,
 * que devuelve los sets con sus fases adentro. Se aplanan de-duplicando por
 * `key`: un set por empresa puede renombrar la columna, pero la `key` y la
 * `category` son las mismas, y lo que decide reglas es la categoría.
 */
export function stageSetsQuery(kind: 'project' | 'task') {
  return {
    queryKey: qk.settings.stageSets({ kind, raw: true }),
    queryFn: () => api.get<StageSetRead[]>(`stage-sets?kind=${kind}`),
    staleTime: 10 * 60_000,
  }
}

/** Igual que con los usuarios: las fases tienen que estar ANTES de mapear. */
export async function ensureStages(
  client: QueryClient,
  kind: 'project' | 'task',
): Promise<Stage[]> {
  try {
    return stagesFromSets(await client.ensureQueryData(stageSetsQuery(kind)))
  } catch {
    return []
  }
}

export function useStageSetsOfKind(kind: 'project' | 'task') {
  return useQuery(stageSetsQuery(kind))
}

export function useProjectStages() {
  return useQuery({ ...stageSetsQuery('project'), select: stagesFromSets })
}

export function useTaskStages() {
  return useQuery({ ...stageSetsQuery('task'), select: stagesFromSets })
}

/**
 * El detalle de un proyecto.
 *
 * `ProjectRead` viene con ids pelados: `stage_id`, `stage_set_id`,
 * `company_id`, `internal_owner_id`, `client_contact_id`. La pantalla necesita
 * los objetos, así que el detalle se compone con las queries que ya están
 * cacheadas (las fases, los usuarios, la empresa) en vez de pedirle al backend
 * un DTO nuevo.
 *
 * Lo que `ProjectRead` **no** trae son los contadores: `open_tasks` y
 * `overdue_tasks`. Antes se rellenaban con `0`, y un proyecto con seis tareas
 * mostraba "0 tareas" —el QA lo levantó como bloqueante—: un cero es una
 * afirmación, y era falsa. Ahora salen del listado de la empresa
 * (`useCompanyProjects`), que los cuenta en el servidor, y valen `null`
 * mientras ese listado viaja. `null` la pantalla lo dibuja como un guión, que
 * es lo único cierto hasta que llegue el número.
 *
 * `next_due_date` sigue sin viajar: la barra de estado lo saca de las tareas
 * del proyecto, que ya están cargadas en la misma pantalla.
 */
export function useProject(projectId: string) {
  const users = useUserIndex()
  const sets = useStageSetsOfKind('project')
  const raw = useQuery({
    queryKey: qk.projects.detail(projectId),
    queryFn: () => api.get<ProjectRead>(`projects/${projectId}`),
    enabled: Boolean(projectId),
  })
  const company = useCompany(raw.data?.company_id ?? '')
  // Comparte cache y query key con la pestaña de proyectos de la empresa: si se
  // llega al detalle desde ahí, los contadores ya están y no hay pedido nuevo.
  const hermanos = useCompanyProjects(raw.data?.company_id ?? '')
  const contado = hermanos.data?.find((p) => p.id === projectId)
  // `ProjectRead` da el id del contacto, no su nombre ni su mail. Se resuelve
  // contra los contactos de la empresa, que la ficha ya tiene cacheados.
  const contactos = useCompanyContacts(raw.data?.company_id ?? '')

  const stageSet = sets.data?.find((set) => set.id === raw.data?.stage_set_id) ?? sets.data?.[0]
  const stages = stageSet ? toStageSet(stageSet).stages.map((stage) => stage as Stage) : []

  const data: ProjectDetail | undefined = raw.data && {
    id: raw.data.id,
    name: raw.data.name,
    company: company.data ?? {
      id: raw.data.company_id,
      slug: '',
      name: '',
      color_hue: 0,
      status: 'active',
    },
    stage: stages.find((stage) => stage.id === raw.data!.stage_id) ??
      stageSet?.stages.filter((stage) => stage.id === raw.data!.stage_id).map(toStage)[0] ?? {
        id: raw.data.stage_id,
        name: '',
        category: 'backlog',
        position: 0,
        color: null,
      },
    stage_set_id: raw.data.stage_set_id,
    stages,
    is_ongoing: raw.data.is_ongoing,
    position: raw.data.position,
    internal_owner: raw.data.internal_owner_id
      ? (users.get(raw.data.internal_owner_id) ?? null)
      : null,
    internal_owner_id: raw.data.internal_owner_id,
    /**
     * El contacto, resuelto.
     *
     * Estaba fijo en `null` con un comentario que decía "lo resuelve
     * `ProjectStatusBar`" — y no lo resolvía. El resultado era que la ficha de
     * un proyecto **con contacto cargado** mostraba `Sin contacto asignado`,
     * para siempre y para todos. El QA funcional lo levantó como 1.7.
     *
     * Un comentario que delega en otro archivo y nadie verifica es la forma más
     * silenciosa de dejar algo sin hacer: el código se lee bien.
     */
    client_contact: raw.data.client_contact_id
      ? ((contactos.data ?? [])
          .filter((c) => c.id === raw.data!.client_contact_id)
          .map((c) => ({ id: c.id, name: c.name, email: c.email }))[0] ?? null)
      : null,
    next_due_date: null,
    actual_end_date: raw.data.actual_end_date,
    open_tasks: contado?.open_tasks ?? null,
    overdue_tasks: contado?.overdue_tasks ?? null,
    is_client_visible: raw.data.is_client_visible,
    tag_ids: raw.data.tag_ids ?? [],
    client_visible: raw.data.client_visible,
    health: raw.data.health,
    brief_md: raw.data.brief_md,
    client_summary_md: raw.data.client_summary_md,
    internal_notes_md: raw.data.internal_notes_md,
    start_date: raw.data.start_date,
    target_end_date: raw.data.target_end_date,
    created_at: raw.data.created_at,
  }

  return { ...raw, data }
}

/**
 * La actividad del proyecto.
 *
 * `GET /projects/{id}/activity` **no existe**: la trazabilidad vive en el log
 * de auditoría transversal, que se consulta por entidad.
 */
export function useProjectActivity(projectId: string) {
  const users = useUserIndex()

  return useQuery({
    queryKey: qk.projects.activity(projectId),
    queryFn: () =>
      api.get<AuditEntryRead[]>(
        // `projects`, en PLURAL: el `entity_type` de la auditoría es el nombre
        // de la TABLA, no el del modelo. Lo decide `audit.events._tabla_de()`,
        // que lo elige así a propósito ("el historial lo van a leer personas
        // que miran la base"). Pedir `project` no devolvía un error: devolvía
        // una lista vacía, y la tab Actividad estuvo vacía desde siempre sin
        // que nada se quejara.
        `audit/entries?entity_type=projects&entity_id=${encodeURIComponent(projectId)}&limit=50`,
      ),
    enabled: Boolean(projectId),
    /*
      Se descartan las líneas cuyo ÚNICO cambio es ruido.

      Cada arrastre de prioridad escribe una línea de auditoría con
      `priority_rank: h → d`, que `diffDeAuditoria` esconde por inútil. El
      problema es lo que queda: una entrada sin nada debajo que dice "hizo un
      cambio" y no cuenta cuál. En un proyecto que se reordena seguido, esas
      entradas tapan las que sí importan.

      La condición pide `before` Y `after`: así se descarta sólo lo que ERA un
      update con campos ocultos. Las altas y los borrados no tienen diff por
      definición y se quedan — ahí "pasó algo" ES la información.
    */
    select: (entries): ActivityEntry[] =>
      entries
        .map((entry) => ({ dto: entry, adaptada: toActivityEntry(entry, users) }))
        .filter(({ dto, adaptada }) => !(dto.before && dto.after && adaptada.changes.length === 0))
        .map(({ adaptada }) => adaptada),
  })
}

export interface ProjectPatch {
  stage_id?: string
  is_client_visible?: boolean
  internal_owner_id?: string | null
}

/**
 * Cambiar la fase o la visibilidad desde la barra de estado.
 *
 * Dos endpoints distintos, porque el backend los separó a propósito:
 * `PATCH /projects/{id}` **no acepta `stage_id`** (mover de fase es
 * `POST /projects/{id}/move`, que además ordena) ni `is_client_visible`
 * (`PATCH /projects/{id}/visibility`, que es la decisión deliberada de la
 * invariante). Un solo PATCH que hiciera las tres cosas convertiría un acto
 * explícito en un campo más de un formulario.
 *
 * Optimista sobre el detalle, porque el pill es de UN CLICK y esperar la
 * respuesta para repintarlo hace que se sienta roto.
 */
export function useUpdateProject(projectId: string) {
  const client = useQueryClient()
  const t = useT()

  return useMutation({
    mutationFn: async (patch: ProjectPatch): Promise<ProjectRead> => {
      if (patch.stage_id !== undefined) {
        return api.post<ProjectRead>(`projects/${projectId}/move`, { stage_id: patch.stage_id })
      }
      if (patch.is_client_visible !== undefined) {
        return api.patch<ProjectRead>(`projects/${projectId}/visibility`, {
          is_client_visible: patch.is_client_visible,
        })
      }
      return api.patch<ProjectRead>(`projects/${projectId}`, {
        internal_owner_id: patch.internal_owner_id,
      })
    },
    onMutate: async (patch) => {
      await client.cancelQueries({ queryKey: qk.projects.detail(projectId) })
      const previous = client.getQueryData<ProjectRead>(qk.projects.detail(projectId))

      if (previous) {
        client.setQueryData<ProjectRead>(qk.projects.detail(projectId), {
          ...previous,
          stage_id: patch.stage_id ?? previous.stage_id,
          is_client_visible: patch.is_client_visible ?? previous.is_client_visible,
          internal_owner_id:
            patch.internal_owner_id !== undefined
              ? patch.internal_owner_id
              : previous.internal_owner_id,
        })
      }

      return { previous }
    },
    onError: (error, _patch, context) => {
      if (context?.previous) client.setQueryData(qk.projects.detail(projectId), context.previous)
      // El mensaje de la API ya viene traducido del backend; el fallback es
      // nuestro y por eso sale del catálogo.
      toast.error(errorMessage(error, t('projects.error.save')))
    },
    onSettled: () => {
      void client.invalidateQueries({ queryKey: qk.projects.detail(projectId) })
      void client.invalidateQueries({ queryKey: qk.portfolio.all })
    },
  })
}

export { patchFromProjectRead }

export interface ProjectCreateInput {
  company_id: string
  name: string
  internal_owner_id?: string | null
  client_contact_id?: string | null
  is_ongoing?: boolean
  start_date?: string | null
  target_end_date?: string | null
  brief_md?: string | null
  /** Clave de plantilla: aplica la arquitectura al crear, en un solo paso. */
  architecture_template?: string | null
}

/**
 * Alta de proyecto.
 *
 * `is_client_visible` **no está** en el formulario ni en el contrato de alta, y
 * no es un olvido: el backend no lo acepta en `POST /projects` por la misma
 * razón que no lo acepta en `PATCH`. Todo nace interno y hacerse visible al
 * cliente es un acto explícito, con su propio endpoint
 * (`PATCH /projects/{id}/visibility`). Ponerlo acá sería convertir la
 * invariante del sistema en una casilla más de un alta.
 *
 * `architecture_template` sí está: aplicar la plantilla al crear ahorra el paso
 * que el QA tuvo que dar por separado, y es aditivo — sin plantilla el proyecto
 * nace vacío.
 *
 * Invalida por prefijo (`qk.projects.lists()`, `qk.portfolio.all`, y los proyectos de
 * la empresa): un proyecto nuevo tiene que aparecer en el portafolio, en el
 * tablero, en prioridades y en la ficha de su empresa, y cada una de esas
 * pantallas cachea con sus propios filtros.
 */
export interface ProjectEditInput {
  name?: string
  brief_md?: string | null
  internal_owner_id?: string | null
  client_contact_id?: string | null
  start_date?: string | null
  target_end_date?: string | null
  health?: 'green' | 'yellow' | 'red' | null
  internal_notes_md?: string | null
}

/**
 * Edita la ficha completa. **Aparte de `useUpdateProject`, a propósito.**
 *
 * Aquel resuelve los tres cambios de un clic de la barra de estado —fase,
 * visibilidad y responsable—, cada uno con su endpoint y con actualización
 * optimista escrita campo por campo. Meter ahí un patch de ocho campos
 * obligaría a extender ese optimismo a todos, y el primero que se olvidara
 * dejaría la pantalla mostrando un valor viejo como si estuviera guardado.
 *
 * Este no es optimista: se guarda, se espera y se relee. Es un formulario, no
 * un clic: nadie está esperando que la pantalla responda en el mismo frame.
 */
export function useEditProject(projectId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (input: ProjectEditInput) =>
      api.patch<ProjectRead>(`projects/${projectId}`, { ...input }),
    onSuccess: (proyecto) => {
      void client.invalidateQueries({ queryKey: qk.projects.detail(projectId) })
      void client.invalidateQueries({ queryKey: qk.projects.lists() })
      // El nombre, el responsable y las fechas viajan en el portafolio, la
      // línea de tiempo y el tablero.
      void client.invalidateQueries({ queryKey: qk.portfolio.all })
      void client.invalidateQueries({ queryKey: qk.companies.detail(proyecto.company_id) })
    },
  })
}

/**
 * Archiva el proyecto (borrado suave).
 *
 * Lo saca del portafolio, del tablero y del ranking de prioridad. No borra sus
 * tareas ni sus documentos: quedan colgando del proyecto archivado, que es lo
 * que permite volver a mirarlo. Por eso la pantalla pregunta antes.
 */
export function useDeleteProject() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (projectId: string) => api.delete(`projects/${projectId}`),
    onSuccess: (_data, projectId) => {
      void client.invalidateQueries({ queryKey: qk.projects.detail(projectId) })
      void client.invalidateQueries({ queryKey: qk.projects.lists() })
      void client.invalidateQueries({ queryKey: qk.portfolio.all })
    },
  })
}

export function useCreateProject() {
  const client = useQueryClient()
  const t = useT()

  return useMutation({
    mutationFn: (input: ProjectCreateInput) => api.post<ProjectRead>('projects', { ...input }),
    onSuccess: (created) => {
      void client.invalidateQueries({ queryKey: qk.projects.lists() })
      void client.invalidateQueries({ queryKey: qk.portfolio.all })
      void client.invalidateQueries({ queryKey: qk.companies.detail(created.company_id) })
    },
    onError: (error) => {
      toast.error(errorMessage(error, t('projects.error.create')))
    },
  })
}
